import { Spinner, TableCell, TableHead, TableRow } from '@/components/ui'
import { cn } from '@/lib/utils'
import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useMemo } from 'react'
import { useNavigate } from 'react-router'
import { formatNumber } from '../driver-grade/metrics'
import { filterDrivers, useDriverWorkFilterForm } from './driver-work-filter'
import { DRIVER_WORK_CHARTS_PATH, DriverWorkLayout } from './driver-work-header'
import { DRIVER_WORK_YEAR_QUERY } from './queries'
import { ScrollTable } from './scroll-table'
import { useRowSort } from './row-sort'
import { yearWeeks } from './year-weeks'

const STICKY = 'sticky left-0 z-10 border-r border-neutral-200'

export const DriverWorkWeeksPage: React.FC = () => {
  const translations = useTranslate({
    driver: 'common.driver',
    week: 'common.week',
    total: 'driver-work.total',
    noData: 'driver-work.no-data',
  })
  const form = useDriverWorkFilterForm()
  const { year, drivers, ownedVehiclesOnly } = form.filter
  const navigate = useNavigate()
  const {
    data: current,
    previousData,
    loading,
  } = useQuery(DRIVER_WORK_YEAR_QUERY, {
    variables: { year, ownedVehiclesOnly },
  })
  const data = current ?? previousData

  const weeks = useMemo(() => yearWeeks(year, []), [year])
  const rows = useMemo(
    () =>
      filterDrivers(data?.driverWorkYear ?? [], drivers).map(row => ({
        ...row,
        byWeek: new Map(
          row.weekTotals.map(week => [week.weekStart, week.totalHours])
        ),
      })),
    [data, drivers]
  )
  const { sorted, headProps } = useRowSort<(typeof rows)[number], string>(
    rows,
    {
      name: row => row.user.fullName,
      total: row => row.totalHours,
      ...Object.fromEntries(
        weeks.map(week => [
          week.weekStart,
          (row: (typeof rows)[number]) => row.byWeek.get(week.weekStart) ?? 0,
        ])
      ),
    },
    { key: 'name', desc: false }
  )

  const openWeek = (user: string, weekStart: string) => {
    const params = new URLSearchParams({ user, week: weekStart })
    void navigate(`${DRIVER_WORK_CHARTS_PATH}?${params}`)
  }

  return (
    <DriverWorkLayout form={form}>
      <ScrollTable
        className="text-sm"
        header={
          <TableRow>
            <TableHead
              className={cn(STICKY, 'z-30 bg-quaternary')}
              {...headProps('name')}
            >
              {translations.driver}
            </TableHead>
            {weeks.map(week => (
              <TableHead
                key={week.weekStart}
                end
                className="px-3"
                {...headProps(week.weekStart)}
              >
                {translations.week} {week.isoWeek}
              </TableHead>
            ))}
            <TableHead end {...headProps('total')}>
              {translations.total}
            </TableHead>
          </TableRow>
        }
      >
        {loading && !data && (
          <TableRow>
            <TableCell colSpan={weeks.length + 2}>
              <Spinner />
            </TableCell>
          </TableRow>
        )}
        {data && !rows.length && (
          <TableRow>
            <TableCell
              colSpan={weeks.length + 2}
              className="text-center text-stone-500"
            >
              {translations.noData}
            </TableCell>
          </TableRow>
        )}
        {sorted.map(row => (
          <TableRow key={row.user.id} className="group">
            <TableCell
              className={cn(STICKY, 'bg-white group-hover:bg-stone-50')}
            >
              {row.user.fullName}
            </TableCell>
            {weeks.map(week => {
              const hours = row.byWeek.get(week.weekStart) ?? 0
              return (
                <TableCell
                  key={week.weekStart}
                  end
                  className="p-0 group-hover:bg-stone-50"
                >
                  {hours ? (
                    <button
                      type="button"
                      onClick={() => openWeek(row.user.id, week.weekStart)}
                      className="w-full px-3 py-3 text-right tabular-nums hover:bg-stone-200"
                    >
                      {formatNumber(hours, 2)}
                    </button>
                  ) : (
                    <span className="block px-3 py-3 tabular-nums text-stone-300">
                      {formatNumber(0, 2)}
                    </span>
                  )}
                </TableCell>
              )
            })}
            <TableCell
              end
              className="font-semibold tabular-nums group-hover:bg-stone-50"
            >
              {formatNumber(row.totalHours, 2)}
            </TableCell>
          </TableRow>
        ))}
      </ScrollTable>
    </DriverWorkLayout>
  )
}
